import { useLayoutEffect } from "react";

const keyboardThreshold = 80;

/**
 * Mobile Safari can leave 100dvh at the layout viewport height while its
 * keyboard overlays the visual viewport. Keep CSS aligned to the part of the
 * page that is actually visible so fixed-in-app controls remain above it.
 */
export function useVisualViewport(): void {
  useLayoutEffect(() => {
    const root = document.documentElement;
    let animationFrame = 0;

    const measure = () => {
      animationFrame = 0;
      const viewport = window.visualViewport;
      const height = viewport?.height ?? window.innerHeight;
      const offsetTop = viewport?.offsetTop ?? 0;
      const coveredHeight = viewport
        ? Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop)
        : 0;
      const editableFocused = document.activeElement instanceof HTMLInputElement
        || document.activeElement instanceof HTMLTextAreaElement;

      root.style.setProperty("--visual-viewport-height", `${height}px`);
      root.style.setProperty("--visual-viewport-offset-top", `${offsetTop}px`);
      root.dataset.compactViewport = height < 620 ? "true" : "false";
      root.dataset.keyboardOpen = editableFocused && coveredHeight > keyboardThreshold ? "true" : "false";
    };

    const scheduleMeasure = () => {
      if (animationFrame) cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener("resize", scheduleMeasure);
    window.addEventListener("orientationchange", scheduleMeasure);
    document.addEventListener("focusin", scheduleMeasure);
    document.addEventListener("focusout", scheduleMeasure);
    window.visualViewport?.addEventListener("resize", scheduleMeasure);
    window.visualViewport?.addEventListener("scroll", scheduleMeasure);

    return () => {
      if (animationFrame) cancelAnimationFrame(animationFrame);
      window.removeEventListener("resize", scheduleMeasure);
      window.removeEventListener("orientationchange", scheduleMeasure);
      document.removeEventListener("focusin", scheduleMeasure);
      document.removeEventListener("focusout", scheduleMeasure);
      window.visualViewport?.removeEventListener("resize", scheduleMeasure);
      window.visualViewport?.removeEventListener("scroll", scheduleMeasure);
      delete root.dataset.compactViewport;
      delete root.dataset.keyboardOpen;
      root.style.removeProperty("--visual-viewport-height");
      root.style.removeProperty("--visual-viewport-offset-top");
    };
  }, []);
}

